import { alertService, ecoSettingService } from "client-app-core";
import * as t from "./actionTypes.js";
export { removeDockedCameraAndState } from "orion-components/Dock/Actions/index.js";
import { updateContext } from "orion-components/ContextualData/Actions";
import objectHash from "object-hash";

const getActiveAlertFingerprint = state => {
	const activeItemsById = state?.globalData?.alerts?.activeItemsById || {};
	const alerts = Object.values(activeItemsById)
		.filter(alert => alert && !alert.closed)
		.map(alert => [alert.id, alert.lastModifiedDate || null])
		.sort(([a], [b]) => String(a).localeCompare(String(b)));

	return {
		hash: objectHash(alerts),
		count: alerts.length
	};
};

const parseEnabledSetting = value => {
	if (typeof value === "string") {
		return value.toLowerCase() !== "false";
	}
	return value !== false;
};

export function startAlertStream() {
	return function (dispatch, getState) {
		let subscription = null;
		let lastJsSeqSeen = 0;
		let resyncInFlight = false;
		let syncStatusTimer = null;
		let syncStatusInFlight = false;
		let driftCheck = { enabled: true, interval: 30000 };
		let lastFingerprintSentAt = 0;
		let syncStatusInterval = 5000;

		const loadDriftCheckConfig = () => {
			ecoSettingService.getByKey("alertStreamDriftCheckEnabled", (enabledErr, enabledResponse) => {
				ecoSettingService.getByKey("alertStreamDriftCheckInterval", (intervalErr, intervalResponse) => {
					const enabled = enabledResponse?.value;
					const intervalValue = intervalResponse?.value;
					const interval = Number(intervalErr ? 30000 : intervalValue);
					driftCheck = {
						enabled: enabledErr ? true : parseEnabledSetting(enabled),
						interval: Number.isFinite(interval) && interval > 0 ? interval : 30000
					};
				});
			});
		};

		const loadSyncStatusConfig = () => {
			ecoSettingService.getByKey("alertStreamSyncStatusInterval", (err, intervalResponse) => {
				const intervalValue = intervalResponse?.value;
				const interval = Number(err ? 5000 : intervalValue);
				syncStatusInterval = Number.isFinite(interval) && interval > 0 ? interval : 5000;
			});
		};

		const getActiveFingerprintIfDue = () => {
			if (!driftCheck.enabled) return null;

			const now = Date.now();
			if (lastFingerprintSentAt && now - lastFingerprintSentAt < driftCheck.interval) return null;

			lastFingerprintSentAt = now;
			return getActiveAlertFingerprint(getState());
		};

		const applyAlertBatch = response => {
			switch (response.type) {
				case "initial-batch":
				case "resync-batch": {
					const alerts = (response.changes || []).map(item => {
						item.new_val.isPriority = true;
						return item.new_val;
					});
					global.isInitialAlertsLoaded = false;
					dispatch(initialAlertsReceived(alerts));
					break;
				}
				case "replay-batch":
				case "change-batch": {
					const alerts = (response.changes || []).map(item => {
						if (item.type === "remove") {
							if (getState().contextualData[item.old_val.id]) {
								dispatch(updateContext(item.old_val.id, item.old_val));
							}
							return item.old_val;
						}
						global.isInitialAlertsLoaded = true;
						item.new_val.isPriority = true;
						if (getState().contextualData[item.new_val.id]) {
							dispatch(updateContext(item.new_val.id, item.new_val));
						}
						return item.new_val;
					});
					dispatch(alertBatchReceived(alerts));
					break;
				}
				default:
			}
		};

		const updateJsSeqCheckpoint = response => {
			if (response?.latestJsSeq) {
				lastJsSeqSeen = Math.max(lastJsSeqSeen, response.latestJsSeq);
			}
			if (response?.jsSeq) {
				lastJsSeqSeen = Math.max(lastJsSeqSeen, response.jsSeq);
			}
		};

		const resyncAlertStream = () => {
			if (!subscription || resyncInFlight) return;
			resyncInFlight = true;
			alertService
				.resyncActiveAlerts(subscription)
				.then(response => {
					if (response?.success) {
						applyAlertBatch(response);
						updateJsSeqCheckpoint(response);
					} else {
						console.warn("Alert stream resync failed", response);
					}
				})
				.finally(() => {
					resyncInFlight = false;
				});
		};

		const checkSyncStatus = () => {
			if (!subscription || syncStatusInFlight) return;
			syncStatusInFlight = true;
			alertService
				.getAlertSubscriptionSyncStatus(subscription, lastJsSeqSeen, getActiveFingerprintIfDue())
				.then(response => {
					if (!response?.success) {
						console.warn("Alert stream sync-status failed", response);
						return;
					}

					if (response.type === "replay-batch" || response.type === "resync-batch") {
						applyAlertBatch(response);
					}
					updateJsSeqCheckpoint(response);
				})
				.finally(() => {
					syncStatusInFlight = false;
					scheduleSyncStatus();
				});
		};

		const scheduleSyncStatus = () => {
			if (!subscription || syncStatusTimer) return;
			syncStatusTimer = setTimeout(() => {
				syncStatusTimer = null;
				checkSyncStatus();
			}, syncStatusInterval);
		};

		const streamPromise = alertService.streamMyActiveAlerts(function (err, response) {
			if (err) {
				console.log(err);
			} else {
				const jsSeq = response.jsSeq;
				if (jsSeq) {
					if (jsSeq <= lastJsSeqSeen) {
						return;
					}
				}
				applyAlertBatch(response);
				updateJsSeqCheckpoint(response);
			}
		});

		if (streamPromise?.then) {
			loadDriftCheckConfig();
			loadSyncStatusConfig();
			streamPromise.then(sub => {
				subscription = sub;
				const unsubscribe = subscription.unsubscribe;
				scheduleSyncStatus();
				subscription.unsubscribe = async () => {
					if (syncStatusTimer) {
						clearTimeout(syncStatusTimer);
						syncStatusTimer = null;
					}
					return unsubscribe();
				};
			});
		}
	};
}

export const initialAlertsReceived = alerts => {
	return {
		alerts,
		type: t.INITIAL_ALERTS_RECEIVED
	};
};

export const alertBatchReceived = alerts => {
	return {
		alerts,
		type: t.ALERT_BATCH_RECEIVED
	};
};

export const getArchiveSuccess = alerts => {
	return {
		alerts,
		type: t.GET_ALERT_ARCHIVE_SUCCESS
	};
};

export const getArchiveFailed = () => {
	return {
		type: t.GET_ALERT_ARCHIVE_FAILED
	};
};

export const queryArchive = page => {
	return dispatch => {
		alertService.getArchivedByPage(page, (err, response) => {
			if (err) {
				dispatch(getArchiveFailed());
				console.log(err);
			} else {
				dispatch(getArchiveSuccess(response));
			}
		});
	};
};

export const dumpArchive = () => {
	return {
		type: t.DUMP_ALERT_ARCHIVE
	};
};

export const closeAlert = alertId => {
	return {
		alertId,
		type: t.CLOSE_ALERT
	};
};

export const closeBulkAlerts = (alerts, serviceType) => {
	return {
		alerts,
		serviceType,
		type: t.CLOSE_ALERTS
	};
};

export const reopenAlert = alert => {
	return {
		alert,
		notificationId: alert.id,
		type: t.REOPEN_ALERT
	};
};

export const reopenBulkAlerts = alerts => {
	return {
		alerts,
		alertIds: alerts.map(a => a.id),
		type: t.REOPEN_ALERTS
	};
};

export const acknowledgeAlerts = alerts => {
	return () => {
		alertService.acknowledgeNotifications(alerts, err => {
			if (err) {
				console.log(err);
			}
		});
	};
};

export const unAcknowledgeAlerts = alerts => {
	return () => {
		alertService.unAcknowledgeNotifications(alerts, err => {
			if (err) {
				console.log(err);
			}
		});
	};
};
